import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchReport, fetchReportEncounters } from '../api/reports';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { ReportEncounterTable } from '../components/reports/ReportEncounterTable';
import type { EncounterSummary } from '../api/types';
import { formatNumber } from '../utils/format';
import { getActiveServerSlug, serverDisplayName, serverGuildPath } from '../utils/siteContext';

const PAGE_SIZE = 50;

function encounterStartedTime(encounter: EncounterSummary) {
  const value = encounter.startedAt ? new Date(encounter.startedAt).getTime() : 0;
  return Number.isFinite(value) ? value : 0;
}

function sortEncountersChronological(encounters: EncounterSummary[]) {
  return [...encounters].sort((a, b) => encounterStartedTime(a) - encounterStartedTime(b));
}

function clampPage(rawPage: string | null, totalPages: number) {
  const parsed = Number.parseInt(rawPage ?? '1', 10);
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return Math.min(parsed, Math.max(totalPages, 1));
}

export function ReportEncountersPage() {
  const { reportId = '' } = useParams<{ reportId?: string }>();
  const [searchParams] = useSearchParams();
  const activeServerSlug = getActiveServerSlug();

  const reportQuery = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => fetchReport(reportId),
    enabled: Boolean(reportId),
  });

  const encountersQuery = useQuery({
    queryKey: ['report', reportId, 'encounters', 'all'],
    queryFn: () => fetchReportEncounters(reportId),
    enabled: Boolean(reportId),
  });

  const report = reportQuery.data?.report;
  const encounters = useMemo(() => sortEncountersChronological(encountersQuery.data ?? []), [encountersQuery.data]);
  const totalPages = Math.max(1, Math.ceil(encounters.length / PAGE_SIZE));
  const page = clampPage(searchParams.get('page'), totalPages);
  const pageEncounters = encounters.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function pageHref(pageNumber: number) {
    return pageNumber <= 1 ? `/reports/${reportId}/encounters` : `/reports/${reportId}/encounters?page=${pageNumber}`;
  }

  return (
    <>
      <section className="page-hero">
        <div className="content-wrapper py-4">
          {reportQuery.isLoading ? <LoadingState label="Loading report" /> : null}
          {reportQuery.isError ? <ErrorState title="Could not load report" error={reportQuery.error} /> : null}

          {report ? (
            <>
              <h1 className="display-6 fw-semibold mb-0">Report Encounters</h1>
              <span className="page-hero__server d-block">{report.server?.name ?? serverDisplayName(activeServerSlug)}</span>
              <p className="fs-3 mb-0">
                {report.guild ? (
                  <>
                    &lt;<Link className="link-light text-decoration-none" to={serverGuildPath(activeServerSlug, report.guild)}>{report.guild.name}</Link>&gt;
                  </>
                ) : (
                  <span>Personal Logs</span>
                )}
              </p>
              <div className="small text-body-secondary mt-2">{report.displayName}</div>
            </>
          ) : null}
        </div>
      </section>

      <div className="content-wrapper page-main">
        <div className="mb-4">
          <Link className="btn btn-sm btn-outline-light" to={`/reports/${reportId}`}>Back to report</Link>
        </div>

        {encountersQuery.isLoading ? <LoadingState label="Loading report encounters" /> : null}
        {encountersQuery.isError ? <ErrorState title="Could not load report encounters" error={encountersQuery.error} /> : null}

        {encountersQuery.data ? (
          <>
            <ReportEncounterTable
              title="All Encounters"
              encounters={pageEncounters}
              activeServerSlug={activeServerSlug}
              emptyMessage="No encounters linked to this report yet."
              footer={<span className="small text-body-secondary">Showing {formatNumber(pageEncounters.length)} of {formatNumber(encounters.length)} encounters</span>}
            />

            {totalPages > 1 ? (
              <nav className="mt-4" aria-label="Report encounters pages">
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <Link className="page-link" to={pageHref(page - 1)} aria-disabled={page <= 1}>Previous</Link>
                  </li>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                    <li className={`page-item ${pageNumber === page ? 'active' : ''}`} key={pageNumber}>
                      <Link className="page-link" to={pageHref(pageNumber)}>{pageNumber}</Link>
                    </li>
                  ))}
                  <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                    <Link className="page-link" to={pageHref(page + 1)} aria-disabled={page >= totalPages}>Next</Link>
                  </li>
                </ul>
              </nav>
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}
